/*
 * Every server call the application makes, as a hook.
 *
 * Two things are worth knowing about the polling intervals below.
 *
 * Prices do NOT come from here. They arrive on the WebSocket in
 * `useLiveQuotes`, and these queries carry the things that change on a human
 * timescale — a position opening, an order filling, a strategy being halted.
 * So the intervals are seconds, not milliseconds, and they exist to catch what
 * the engine did on its own while nobody was looking, not to animate anything.
 *
 * Invalidation is deliberately broad: almost anything that changes one of
 * these changes several. Arming a strategy changes the strategy list, the
 * account status and, within a tick, the positions. Refetching a few small
 * JSON documents costs far less than reasoning about which ones can be left
 * stale, and being wrong about that on a trading screen shows somebody a
 * position that has already been closed.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { api } from "./client";
import type {
  AccountStatus,
  BacktestResult,
  Candles,
  DaySummary,
  Holding,
  Instrument,
  Interval,
  Mode,
  Order,
  Performance,
  Person,
  Position,
  Quote,
  Signal,
  Strategy,
  User,
} from "./types";

const TRADE = "/api/trade";

/** While the market is open, ask more often. Outside it, almost never. */
export function livePoll(marketOpen: boolean | undefined, openMs: number, shutMs = 120_000) {
  return marketOpen ? openMs : shutMs;
}

/* ─────────────────────────── session ─────────────────────────── */

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<{ user: User }>("/api/auth/me"),
    retry: false,
    staleTime: 60_000,
  });
}

export function useRequestOtp() {
  return useMutation({
    mutationFn: (mobile: string) =>
      api.post<{ sent: boolean; expiresIn: number }>("/api/auth/request-otp", { mobile }),
  });
}

export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { mobile: string; otp: string }) =>
      api.post<{ ok: boolean }>("/api/auth/verify-otp", vars),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean }>("/api/auth/logout"),
    onSuccess: () => qc.clear(),
  });
}

/* ─────────────────────────── the account ─────────────────────────── */

export function useStatus() {
  return useQuery({
    queryKey: ["status"],
    queryFn: () => api.get<AccountStatus>(`${TRADE}/status`),
    // Carries the IST clock and the market-open flag, which the header shows.
    refetchInterval: 20_000,
    staleTime: 5_000,
  });
}

export function useSaveCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { apiKey: string; apiSecret: string }) =>
      api.put<AccountStatus>(`${TRADE}/credentials`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["status"] }),
  });
}

export function useConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestToken: string) =>
      api.post<AccountStatus>(`${TRADE}/connect`, { requestToken }),
    // A fresh Zerodha session changes what nearly every screen can show.
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AccountStatus>(`${TRADE}/disconnect`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useForgetAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ ok: boolean }>(`${TRADE}/account`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useSetHalted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { halted: boolean; reason?: string | null }) =>
      api.put<AccountStatus>(`${TRADE}/halt`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["status"] }),
  });
}

export function useSetCapital() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      capitalRupees: number;
      maxDailyLossRupees: number;
      maxTradesPerDay: number;
      paperCapitalRupees: number;
    }) => api.put<AccountStatus>(`${TRADE}/capital`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["status"] }),
  });
}

/** Texts a code to the signed-in number, for the switch to real trading. */
export function useRequestEnvironmentOtp() {
  return useMutation({
    mutationFn: () =>
      api.post<{ sent: boolean; expiresIn: number; mobile: string }>(
        `${TRADE}/environment/request-otp`,
      ),
  });
}

export function useSetEnvironment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { environment: Mode; otp?: string }) =>
      api.put<AccountStatus & { strategiesChanged: number }>(`${TRADE}/environment`, vars),
    // Changing environment changes what every single screen should be showing.
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useFunds(enabled: boolean) {
  return useQuery({
    queryKey: ["funds"],
    queryFn: () => api.get<Record<string, unknown>>(`${TRADE}/funds`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useHoldings(enabled: boolean) {
  return useQuery({
    queryKey: ["holdings"],
    queryFn: () => api.get<Holding[]>(`${TRADE}/holdings`),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/* ─────────────────────────── instruments ─────────────────────────── */

export function useInstrumentSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: ["instruments", q],
    queryFn: () =>
      api.get<Instrument[]>(`${TRADE}/instruments?q=${encodeURIComponent(q)}&limit=40`),
    enabled: enabled && q.trim().length >= 1,
    // The exchange's list. It does not change between two keystrokes.
    staleTime: 5 * 60_000,
  });
}

export function useRefreshInstruments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ count: number }>(`${TRADE}/instruments/refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["instruments"] }),
  });
}

/* ─────────────────────────── strategies ─────────────────────────── */

export function useStrategies() {
  return useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<Strategy[]>(`${TRADE}/strategies`),
    refetchInterval: 30_000,
  });
}

export function useStrategy(id: string | undefined) {
  return useQuery({
    queryKey: ["strategy", id],
    queryFn: () => api.get<Strategy>(`${TRADE}/strategies/${id}`),
    enabled: !!id,
  });
}

export function useCreateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<Strategy>(`${TRADE}/strategies`, body),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useUpdateStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: Record<string, unknown> }) =>
      api.put<Strategy>(`${TRADE}/strategies/${vars.id}`, vars.body),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useDeleteStrategy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`${TRADE}/strategies/${id}`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useSetEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; isEnabled: boolean }) =>
      api.put<Strategy>(`${TRADE}/strategies/${vars.id}/enabled`, { isEnabled: vars.isEnabled }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useResumeDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean }>(`${TRADE}/strategies/${id}/resume-day`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useAddWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; instrumentToken: number }) =>
      api.post<Strategy>(`${TRADE}/strategies/${vars.id}/watch`, {
        instrumentToken: vars.instrumentToken,
      }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRemoveWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; instrumentToken: number }) =>
      api.del<Strategy>(`${TRADE}/strategies/${vars.id}/watch/${vars.instrumentToken}`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useBacktest() {
  return useMutation({
    mutationFn: (vars: { id: string; instrumentToken: number; days: number }) =>
      api.post<BacktestResult>(`${TRADE}/strategies/${vars.id}/backtest`, {
        instrumentToken: vars.instrumentToken,
        days: vars.days,
      }),
  });
}

/* ─────────────────────────── running it ─────────────────────────── */

export function useRunNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Record<string, unknown>>(`${TRADE}/run`),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function usePanic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (closePositions: boolean) =>
      api.post<Record<string, unknown>>(`${TRADE}/panic`, { closePositions }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/* ─────────────────────────── reading it back ─────────────────────────── */

export function useSummary(marketOpen?: boolean) {
  return useQuery({
    queryKey: ["summary"],
    queryFn: () => api.get<DaySummary>(`${TRADE}/summary`),
    refetchInterval: livePoll(marketOpen, 15_000),
  });
}

export function usePerformance(marketOpen?: boolean) {
  return useQuery({
    queryKey: ["performance"],
    queryFn: () => api.get<Performance>(`${TRADE}/performance`),
    refetchInterval: livePoll(marketOpen, 30_000, 300_000),
  });
}

/**
 * `mode` is left off deliberately: the server answers with whichever
 * environment the account is in, which is what an environment means. Pass
 * "all" to see both books at once.
 *
 * The environment is part of the cache key even though it is not in the URL —
 * without it, switching would show the previous environment's positions from
 * cache until the next refetch, which on a real-money screen is the wrong kind
 * of wrong.
 */
export function usePositions(
  openOnly: boolean,
  marketOpen?: boolean,
  limit = 200,
  mode?: Mode | "all",
) {
  const environment = useStatus().data?.environment;
  return useQuery({
    queryKey: ["positions", openOnly, limit, mode ?? environment],
    queryFn: () =>
      api.get<Position[]>(
        `${TRADE}/positions?openOnly=${openOnly}&limit=${limit}${mode ? `&mode=${mode}` : ""}`,
      ),
    refetchInterval: livePoll(marketOpen, 15_000),
  });
}

export function useOrders(limit = 100, marketOpen?: boolean, mode?: Mode | "all") {
  const environment = useStatus().data?.environment;
  return useQuery({
    queryKey: ["orders", limit, mode ?? environment],
    queryFn: () =>
      api.get<Order[]>(`${TRADE}/orders?limit=${limit}${mode ? `&mode=${mode}` : ""}`),
    refetchInterval: livePoll(marketOpen, 15_000),
  });
}

export function useSignals(limit = 100, marketOpen?: boolean) {
  return useQuery({
    queryKey: ["signals", limit],
    queryFn: () => api.get<Signal[]>(`${TRADE}/signals?limit=${limit}`),
    refetchInterval: livePoll(marketOpen, 15_000),
  });
}

export function useExitPosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; price?: number | null }) =>
      api.post<Position>(`${TRADE}/positions/${vars.id}/exit`, { price: vars.price ?? null }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/* ─────────────────────────── charts ─────────────────────────── */

export function useCandles(
  token: number | undefined,
  interval: Interval,
  days: number,
  options?: Partial<UseQueryOptions<Candles>>,
) {
  return useQuery({
    queryKey: ["candles", token, interval, days],
    queryFn: () =>
      api.get<Candles>(`/api/market/candles?token=${token}&interval=${interval}&days=${days}`),
    enabled: !!token,
    // A closed bar never changes. Only the newest one does, and the live
    // stream is what extends that — so this can be cached hard.
    staleTime: 60_000,
    retry: false,
    ...options,
  });
}

/** A one-off REST quote, to fill a screen before the socket has connected. */
export function useSeedQuotes(tokens: number[], enabled: boolean) {
  const key = tokens.slice().sort((a, b) => a - b).join(",");
  return useQuery({
    queryKey: ["seed-quotes", key],
    queryFn: () => api.get<{ quotes: Quote[] }>(`/api/market/quote?tokens=${key}`),
    enabled: enabled && tokens.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

/* ─────────────────────────── people ─────────────────────────── */

export function usePeople(enabled: boolean) {
  return useQuery({
    queryKey: ["people"],
    queryFn: () => api.get<Person[]>("/api/admin/people"),
    enabled,
  });
}

export function useAddPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; mobile: string; isAdmin: boolean }) =>
      api.post<Person>("/api/admin/people", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people"] }),
  });
}

export function useEditPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: Record<string, unknown> }) =>
      api.put<Person>(`/api/admin/people/${vars.id}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people"] }),
  });
}

export function useRemovePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ ok: boolean }>(`/api/admin/people/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people"] }),
  });
}
